export type DocumentVisibility = "private" | "shared" | "public";
export type DocumentConvertMode =
  | "auto"
  | "text-only"
  | "no-external-provider"
  | "prefer-external"
  | "force-external"
  | "force-ocr";
export type DocumentConvertProvider =
  | "auto"
  | "local_text"
  | "kordoc"
  | "opendataloader"
  | "gemini"
  | "openai"
  | "ocr"
  | "custom_vlm";
export type DocumentResolvedProvider = Exclude<DocumentConvertProvider, "auto">;
export type DocumentProviderRoute = "local_text" | "kordoc" | "opendataloader" | "vlm" | "ocr_fallback";
export type DocumentCacheArtifact = "convert" | "embedding" | "summary" | "rerank";

export type DocumentConvertInput = {
  storageId: string;
  filename?: string;
  mimeType?: string;
  corpus: string;
  visibility: DocumentVisibility;
  ownerUserId?: string;
  mode?: DocumentConvertMode;
  provider?: DocumentConvertProvider;
  /**
   * Default false.
   * When false, provider:auto must not call gemini/openai/ocr/custom_vlm.
   */
  paidFallback?: boolean;
  /**
   * Optional request-level service credit cap.
   * Provider call must fail before execution when the estimate exceeds this cap.
   */
  maxServiceCredits?: number;
};

export type DocumentPage = {
  page: number;
  charStart: number;
  charEnd: number;
  previewText?: string;
};

export type DocumentSection = {
  id: string;
  title?: string;
  depth: number;
  path: string[];
  pageStart?: number;
  pageEnd?: number;
  charStart: number;
  charEnd: number;
};

export type DocumentQualityDecision = "accept" | "fallback";

export type DocumentQualityTrace = {
  score: number;
  decision: DocumentQualityDecision;
  signals: Record<string, number>;
};

export type DocumentProviderTrace = {
  provider: DocumentResolvedProvider;
  route: DocumentProviderRoute;
  external: boolean;
  requestId?: string;
  durationMs: number;
  bytes: number;
  quality?: DocumentQualityTrace;
};

export type DocumentConvertResult = {
  text: string;
  markdown: string;
  pages: DocumentPage[];
  sections: DocumentSection[];
  warnings: string[];
  providerTrace: DocumentProviderTrace;
};

export interface GencowServicesCtx {}

export interface WorkflowDocumentServicesCtx {
  document: {
    convert(input: DocumentConvertInput): Promise<DocumentConvertResult>;
  };
}

export type DocumentCacheKeyInput = {
  appId: string;
  ownerUserId?: string | null;
  corpus: string;
  sourceKey: string;
  checksum: string;
  provider: DocumentResolvedProvider;
  artifact: DocumentCacheArtifact;
};

function normalizeCachePart(value: string): string {
  return value.trim() || "unknown";
}

export function buildDocumentCacheKey(input: DocumentCacheKeyInput): string {
  const owner = input.ownerUserId?.trim() ? input.ownerUserId.trim() : "shared";

  return [
    `app:${normalizeCachePart(input.appId)}`,
    `user:${normalizeCachePart(owner)}`,
    `corpus:${normalizeCachePart(input.corpus)}`,
    `source:${normalizeCachePart(input.sourceKey)}`,
    `checksum:${normalizeCachePart(input.checksum)}`,
    `provider:${normalizeCachePart(input.provider)}`,
    `artifact:${normalizeCachePart(input.artifact)}`,
  ].join(":");
}
